import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { SupabaseSession } from "../../../lib/supabase";
import type { RealEstateCase } from "../../../types";
import { calculateSimilarityScore } from "../calculations/similarity";
import { buildMarketStrategies, calculateValuation } from "../calculations/valuation";
import {
  clearPendingMarketAnalysisWipe,
  loadMarketAnalyses,
  loadPendingMarketAnalysisWipe,
  queueMarketAnalysisWipe,
  saveMarketAnalyses
} from "../data/localMarketAnalysisStore";
import {
  deleteCloudMarketAnalyses,
  marketAnalysisCollectionsMatch,
  syncMarketAnalysesWithSupabase
} from "../data/marketAnalysisRepository";
import {
  runCandidatePipeline,
  unavailableMarketDataProvider,
  type CandidatePipelineResult,
  type MarketDataProvider
} from "../data/marketDataProvider";
import type { MarketFileImportProvider } from "../data/realityRadar/RealityRadarXlsxProvider";
import type { RealityRadarImportPipelineResult } from "../data/realityRadar/types";
import { activeMarketComparables, isAutomaticComparable, replaceActiveAutomaticComparables } from "../domain/comparableLifecycle";
import { MARKET_QUALIFICATION_POLICY } from "../domain/selectionPolicy";
import {
  MARKET_METHODOLOGY_VERSION,
  type MarketAnalysis,
  type MarketAnalysisComparable,
  type MarketComparableOrigin,
  type MarketComparableRejectionReason,
  type MarketComparableDraft,
  type MarketFunnelSnapshot,
  type MarketSubjectSnapshot
} from "../domain/types";
import {
  createSubjectFromCase,
  snapshotHash,
  validateComparable,
  validateFinalization
} from "../domain/validation";

export type MarketSyncStatus = "Synced" | "Syncing" | "Offline";

export type MarketAnalysisController = {
  analyses: MarketAnalysis[];
  syncStatus: MarketSyncStatus;
  createDraft: (caseItem: RealEstateCase) => MarketAnalysis;
  updateSubject: (analysisId: string, subject: MarketSubjectSnapshot) => void;
  upsertComparable: (analysisId: string, draft: MarketComparableDraft, comparableId?: string) => string[];
  removeComparable: (analysisId: string, comparableId: string) => void;
  setComparableDecision: (analysisId: string, comparableId: string, decision: "included" | "excluded", reason?: MarketComparableRejectionReason) => void;
  analyzeMarket: (analysisId: string) => Promise<CandidatePipelineResult>;
  importRealityRadar: (analysisId: string, file: File) => Promise<RealityRadarImportPipelineResult>;
  calculateResult: (analysisId: string) => MarketAnalysis | undefined;
  finalizeAnalysis: (analysisId: string) => { analysis?: MarketAnalysis; errors: string[] };
  clearAllData: () => void;
};

export const MarketAnalysisContext = createContext<MarketAnalysisController | null>(null);

export const useMarketAnalysisContext = () => {
  const value = useContext(MarketAnalysisContext);
  if (!value) throw new Error("MarketAnalysisContext nie je dostupný.");
  return value;
};

const createUuid = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (character) =>
    (Number(character) ^ Math.floor(Math.random() * 16) >> Number(character) / 4).toString(16)
  );
};

const providerPropertyId = (value?: Record<string, unknown>) =>
  typeof value?.providerPropertyId === "string" ? value.providerPropertyId : undefined;

const comparableFromDraft = (
  analysis: MarketAnalysis,
  draft: MarketComparableDraft,
  origin: MarketComparableOrigin,
  sortOrder: number,
  sourceCutoffAt: string,
  now: string,
  existing?: MarketAnalysisComparable
): MarketAnalysisComparable => {
  const sourceHash = snapshotHash({ ...draft, capturedAt: draft.capturedAt });
  const comparableBase = {
    ...draft,
    origin,
    marketRole: draft.marketRole ?? existing?.marketRole ?? "primary" as const,
    marketContextGroup: draft.marketContextGroup ?? existing?.marketContextGroup,
    primaryQualificationReasons: draft.primaryQualificationReasons ?? existing?.primaryQualificationReasons,
    id: existing?.id ?? createUuid(),
    analysisId: analysis.id,
    userId: analysis.userId,
    sortOrder,
    sourceHash,
    createdAt: existing?.createdAt ?? now,
    updatedAt: now,
    decision: existing?.decision ?? "included" as const,
    rejectionReason: existing?.rejectionReason
  };
  const scoreBreakdown = calculateSimilarityScore(analysis.subjectSnapshot, comparableBase, sourceCutoffAt);
  return { ...comparableBase, similarityScore: scoreBreakdown.finalScore, scoreBreakdown };
};

export const useMarketAnalysisSync = (
  session: SupabaseSession | null,
  onSessionChange?: (session: SupabaseSession) => void,
  marketDataProvider: MarketDataProvider = unavailableMarketDataProvider,
  marketImportProvider?: MarketFileImportProvider
): MarketAnalysisController => {
  const [analyses, setAnalyses] = useState<MarketAnalysis[]>(loadMarketAnalyses);
  const [syncStatus, setSyncStatus] = useState<MarketSyncStatus>("Offline");
  const analysesRef = useRef(analyses);
  const sessionRef = useRef(session);
  const mountedRef = useRef(true);
  const syncInFlightRef = useRef(false);
  const syncAgainRef = useRef(false);

  analysesRef.current = analyses;
  sessionRef.current = session ?? sessionRef.current;

  const commit = useCallback((next: MarketAnalysis[]) => {
    analysesRef.current = next;
    saveMarketAnalyses(next);
    setAnalyses(next);
  }, []);

  const runSync = useCallback(async () => {
    if (!sessionRef.current) {
      if (mountedRef.current) setSyncStatus("Offline");
      return;
    }
    if (syncInFlightRef.current) {
      syncAgainRef.current = true;
      return;
    }

    syncInFlightRef.current = true;
    do {
      syncAgainRef.current = false;
      if (mountedRef.current) setSyncStatus("Syncing");
      try {
        let activeSession = sessionRef.current;
        if (!activeSession) break;
        if (loadPendingMarketAnalysisWipe()) {
          activeSession = await deleteCloudMarketAnalyses(activeSession);
          clearPendingMarketAnalysisWipe();
        }
        const syncInput = analysesRef.current;
        const result = await syncMarketAnalysesWithSupabase(syncInput, activeSession);
        sessionRef.current = result.session;
        onSessionChange?.(result.session);
        if (!mountedRef.current) break;
        if (!marketAnalysisCollectionsMatch(analysesRef.current, syncInput)) {
          syncAgainRef.current = true;
        } else if (!marketAnalysisCollectionsMatch(analysesRef.current, result.analyses)) {
          commit(result.analyses);
        }
        setSyncStatus("Synced");
      } catch {
        if (mountedRef.current) setSyncStatus("Offline");
      }
    } while (syncAgainRef.current && mountedRef.current);
    syncInFlightRef.current = false;
  }, [commit, onSessionChange]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    sessionRef.current = session;
    if (session) void runSync();
    else setSyncStatus("Offline");
  }, [analyses, runSync, session]);

  useEffect(() => {
    const handleOnline = () => void runSync();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") void runSync();
    };
    window.addEventListener("online", handleOnline);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      window.removeEventListener("online", handleOnline);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [runSync]);

  const replaceAnalysis = useCallback((analysis: MarketAnalysis) => {
    commit(analysesRef.current.map((item) => item.id === analysis.id ? analysis : item));
  }, [commit]);

  const createDraft = useCallback((caseItem: RealEstateCase) => {
    const now = new Date().toISOString();
    const version = Math.max(0, ...analysesRef.current.filter((item) => item.caseId === caseItem.id).map((item) => item.version)) + 1;
    const draft: MarketAnalysis = {
      id: createUuid(),
      userId: sessionRef.current?.user.id ?? "local",
      caseId: caseItem.id,
      version,
      status: "draft",
      subjectSnapshot: createSubjectFromCase(caseItem),
      comparables: [],
      strategySnapshot: [],
      methodologyVersion: MARKET_METHODOLOGY_VERSION,
      sourceCutoffAt: now,
      createdAt: now,
      updatedAt: now
    };
    commit([...analysesRef.current, draft]);
    return draft;
  }, [commit]);

  const updateSubject = useCallback((analysisId: string, subject: MarketSubjectSnapshot) => {
    const analysis = analysesRef.current.find((item) => item.id === analysisId);
    if (!analysis || analysis.status === "finalized") return;
    const now = new Date().toISOString();
    const comparables = analysis.comparables.map((comparable) => {
      const scoreBreakdown = calculateSimilarityScore(subject, comparable, analysis.sourceCutoffAt);
      return { ...comparable, similarityScore: scoreBreakdown.finalScore, scoreBreakdown, updatedAt: now };
    });
    replaceAnalysis({
      ...analysis,
      subjectSnapshot: subject,
      comparables,
      marketFunnelSnapshot: undefined,
      resultSnapshot: undefined,
      strategySnapshot: [],
      updatedAt: now
    });
  }, [replaceAnalysis]);

  const upsertComparable = useCallback((analysisId: string, draft: MarketComparableDraft, comparableId?: string) => {
    const errors = validateComparable(draft);
    const analysis = analysesRef.current.find((item) => item.id === analysisId);
    if (!analysis || analysis.status === "finalized") return ["Finalizovanú analýzu už nemožno upravovať."];
    if (errors.length > 0) return errors;
    const now = new Date().toISOString();
    const sourceHash = snapshotHash({ ...draft, capturedAt: draft.capturedAt });
    const existing = comparableId
      ? analysis.comparables.find((item) => item.id === comparableId)
      : analysis.comparables.find((item) => item.sourceHash === sourceHash);
    const comparable = comparableFromDraft(
      analysis,
      draft,
      draft.origin ?? existing?.origin ?? "manual",
      existing?.sortOrder ?? analysis.comparables.length,
      analysis.sourceCutoffAt,
      now,
      existing
    );
    const comparables = existing
      ? analysis.comparables.map((item) => item.id === existing.id ? comparable : item)
      : [...analysis.comparables, comparable];
    replaceAnalysis({ ...analysis, comparables, resultSnapshot: undefined, strategySnapshot: [], updatedAt: now });
    return [];
  }, [replaceAnalysis]);

  const replaceAutomaticCandidates = useCallback((
    analysisId: string,
    candidates: MarketComparableDraft[],
    origin: Exclude<MarketComparableOrigin, "manual">,
    sourceCutoffAt: string,
    marketFunnelSnapshot?: MarketFunnelSnapshot
  ) => {
    const analysis = analysesRef.current.find((item) => item.id === analysisId);
    if (!analysis || analysis.status === "finalized") return;
    const now = new Date().toISOString();
    const previousAutomatic = analysis.comparables.filter(isAutomaticComparable);
    const replacements = candidates.map((candidate, index) => {
      const candidateProviderId = providerPropertyId(candidate.sourceSnapshot);
      const candidateHash = snapshotHash({ ...candidate, capturedAt: candidate.capturedAt });
      const existing = previousAutomatic.find((comparable) =>
        Boolean(candidateProviderId && providerPropertyId(comparable.sourceSnapshot) === candidateProviderId)
        || comparable.sourceHash === candidateHash
      );
      return {
        ...comparableFromDraft(analysis, candidate, origin, index, sourceCutoffAt, now, existing),
        decision: "included" as const,
        rejectionReason: undefined
      };
    });
    const comparables = replaceActiveAutomaticComparables(analysis.comparables, replacements);
    replaceAnalysis({
      ...analysis,
      sourceCutoffAt,
      marketFunnelSnapshot,
      comparables,
      resultSnapshot: undefined,
      strategySnapshot: [],
      updatedAt: now
    });
  }, [replaceAnalysis]);

  const removeComparable = useCallback((analysisId: string, comparableId: string) => {
    const analysis = analysesRef.current.find((item) => item.id === analysisId);
    if (!analysis || analysis.status === "finalized") return;
    const comparables = analysis.comparables
      .filter((item) => item.id !== comparableId)
      .map((item, sortOrder) => ({ ...item, sortOrder }));
    replaceAnalysis({
      ...analysis,
      comparables,
      resultSnapshot: undefined,
      strategySnapshot: [],
      updatedAt: new Date().toISOString()
    });
  }, [replaceAnalysis]);

  const setComparableDecision = useCallback((
    analysisId: string,
    comparableId: string,
    decision: "included" | "excluded",
    rejectionReason?: MarketComparableRejectionReason
  ) => {
    const analysis = analysesRef.current.find((item) => item.id === analysisId);
    if (!analysis || analysis.status === "finalized") return;
    replaceAnalysis({
      ...analysis,
      comparables: analysis.comparables.map((item) => item.id === comparableId
        ? { ...item, decision, rejectionReason: decision === "excluded" ? rejectionReason : undefined, updatedAt: new Date().toISOString() }
        : item),
      resultSnapshot: undefined,
      strategySnapshot: [],
      updatedAt: new Date().toISOString()
    });
  }, [replaceAnalysis]);

  const analyzeMarket = useCallback(async (analysisId: string) => {
    const analysis = analysesRef.current.find((item) => item.id === analysisId);
    if (!analysis || analysis.status === "finalized") {
      return {
        status: "provider-unavailable",
        providerId: "unavailable",
        analyzedCount: 0,
        broadPoolCount: 0,
        selectedCandidateCount: 0,
        prePoolRejectedCount: 0,
        rankedOutCandidateCount: 0,
        excludedCandidateCount: 0,
        ineligibleCandidateCount: 0,
        invalidCandidateCount: 0,
        duplicateCandidateCount: 0,
        eligibleCandidateCount: 0,
        qualificationPolicy: MARKET_QUALIFICATION_POLICY,
        subjectAreaBasis: { value: undefined, basis: "legacy_unspecified", confidence: "low" },
        rejectionReasonCounts: {},
        rejectionPrimaryReasonCounts: {},
        rejectionSamplesByReason: {},
        qualificationAudit: [],
        candidates: [],
        sameConditionMarket: [],
        otherConditionAlternatives: [],
        marketCompetitors: [],
        fallbackSelections: [],
        rejectedCandidates: [],
        sourceStatuses: [],
        rawListingCount: 0,
        normalizedListingCount: 0,
        preliminaryCandidateCount: 0,
        detailListingCount: 0,
        requestCount: 0,
        uniqueListingCount: 0,
        message: "Analýzu nemožno upraviť."
      } as CandidatePipelineResult;
    }
    const result = await runCandidatePipeline(marketDataProvider, analysis.subjectSnapshot, analysis.sourceCutoffAt);
    if (result.status === "ready") replaceAutomaticCandidates(analysisId, [
      ...result.candidates.map((candidate) => ({ ...candidate, marketRole: "primary" as const })),
      ...result.sameConditionMarket,
      ...result.otherConditionAlternatives
    ], "live_provider", analysis.sourceCutoffAt, result.marketFunnel);
    return result;
  }, [marketDataProvider, replaceAutomaticCandidates]);

  const importRealityRadar = useCallback(async (analysisId: string, file: File) => {
    const analysis = analysesRef.current.find((item) => item.id === analysisId);
    if (!analysis || analysis.status === "finalized") throw new Error("Analýzu nemožno upraviť.");
    if (!marketImportProvider) throw new Error("Reality Radar import nie je dostupný.");

    const summary = await marketImportProvider.importFile(file);
    const pipeline = await runCandidatePipeline(
      marketImportProvider,
      analysis.subjectSnapshot,
      summary.capturedAt
    );
    replaceAutomaticCandidates(analysisId, [
      ...pipeline.candidates.map((candidate) => ({ ...candidate, marketRole: "primary" as const })),
      ...pipeline.sameConditionMarket,
      ...pipeline.otherConditionAlternatives
    ], "reality_radar", summary.capturedAt, pipeline.marketFunnel);
    return {
      ...summary,
      relevantCandidates: pipeline.eligibleCandidateCount,
      topCandidates: pipeline.candidates.length,
      excludedCandidates: pipeline.rejectedCandidates.length,
      ineligibleCandidates: pipeline.ineligibleCandidateCount,
      invalidCandidates: pipeline.invalidCandidateCount,
      rankedOutCandidates: pipeline.rankedOutCandidateCount,
      pipeline
    };
  }, [marketImportProvider, replaceAutomaticCandidates]);

  const calculateResult = useCallback((analysisId: string) => {
    const analysis = analysesRef.current.find((item) => item.id === analysisId);
    if (!analysis) return undefined;
    if (analysis.status === "finalized") return analysis;
    const now = new Date().toISOString();
    const resultSnapshot = calculateValuation(
      analysis.subjectSnapshot,
      activeMarketComparables(analysis.comparables).filter((comparable) => comparable.decision !== "excluded"),
      now
    );
    const updated = {
      ...analysis,
      resultSnapshot,
      strategySnapshot: buildMarketStrategies(resultSnapshot),
      updatedAt: now
    };
    replaceAnalysis(updated);
    return updated;
  }, [replaceAnalysis]);

  const finalizeAnalysis = useCallback((analysisId: string) => {
    const calculated = calculateResult(analysisId);
    if (!calculated) return { errors: ["Analýza sa nenašla."] };
    if (calculated.status === "finalized") return { analysis: calculated, errors: [] };
    const errors = validateFinalization(calculated);
    if (errors.length > 0) return { errors };
    const finalizedAt = new Date().toISOString();
    const finalized: MarketAnalysis = {
      ...calculated,
      status: "finalized",
      finalizedAt,
      updatedAt: finalizedAt,
      snapshotHash: snapshotHash({
        subject: calculated.subjectSnapshot,
        comparables: calculated.comparables,
        marketFunnel: calculated.marketFunnelSnapshot,
        result: calculated.resultSnapshot,
        strategy: calculated.strategySnapshot,
        methodologyVersion: calculated.methodologyVersion
      })
    };
    replaceAnalysis(finalized);
    return { analysis: finalized, errors: [] };
  }, [calculateResult, replaceAnalysis]);

  const clearAllData = useCallback(() => {
    queueMarketAnalysisWipe();
    analysesRef.current = [];
    setAnalyses([]);
  }, []);

  return useMemo(() => ({
    analyses,
    syncStatus,
    createDraft,
    updateSubject,
    upsertComparable,
    removeComparable,
    setComparableDecision,
    analyzeMarket,
    importRealityRadar,
    calculateResult,
    finalizeAnalysis,
    clearAllData
  }), [
    analyses,
    calculateResult,
    clearAllData,
    createDraft,
    finalizeAnalysis,
    removeComparable,
    setComparableDecision,
    analyzeMarket,
    importRealityRadar,
    syncStatus,
    updateSubject,
    upsertComparable
  ]);
};
